/**
 * Employee-profile leave-balances panel (#23311). Renders the three
 * balance-tracked leave types (annual/sick/casual) for one employee, with a
 * "Pay out leave" affordance opening the payout drawer.
 *
 * ── Read-only-grant gating (defence in depth)
 *
 * The "Pay out leave" button is hidden entirely when the OAuth grant isn't
 * write-capable (`can_post` from `useLeavePayout()`). The mutation itself
 * additionally rejects a read-only token server-side via the
 * `payroll:employer:write` scope guard on `postLeavePayout` (see
 * `apps/api/src/trpc/routers/payroll.ts:10665`), so hiding the button is UX,
 * not the real security boundary. Balances themselves are visible to
 * read-only grants — this mirrors the "read-only auditor can see leave
 * usage" stance the queue takes.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, EmptyState, GlassCard } from '@jumpstone/ui-react';
import { useLeaveBalances } from '../../data/use_leave_balances';
import { useLeavePayout } from '../../data/use_leave_payout';
import { LeavePayoutDrawer } from './leave_payout_drawer';
import {
  BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY,
  type BalanceTrackedLeaveType,
} from '../../data/wagepro_store_leave_balances';
import { LeaveAdjustDrawer } from '../leave/leave_adjust_drawer';
import type { LeaveAdjustResult } from '../leave/leave_adjust_drawer';
import { buildOpeningBalanceTarget } from '../leave/leave_opening_balance_utils';
import { useAuth } from '../../lib/auth/auth_context';
import { isWriteCapableGrant } from '../../data/use_pay_runs';
import { useOAuthEntityContext } from '../../lib/auth/oauth_entity_context';
import { getTrpcClient } from '../../lib/trpc';

export interface LeaveBalancesPanelProps {
  employee_id: string;
  employee_name: string;
  /** Base currency for the payout monetary display, resolved by the caller from `useSettings()`. */
  currency: string;
}

export function LeaveBalancesPanel({
  employee_id,
  employee_name,
  currency,
}: LeaveBalancesPanelProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, balances, is_loading, retry } = useLeaveBalances(employee_id);
  const payout = useLeavePayout(employee_id);
  const [payout_open, set_payout_open] = useState(false);
  const [adjust_leave_type, set_adjust_leave_type] =
    useState<BalanceTrackedLeaveType | null>(null);
  const { grant } = useAuth();
  const entityContext = useOAuthEntityContext();
  const can_write = isWriteCapableGrant(grant);
  const readyEntity = entityContext.status === 'ready' ? entityContext : null;

  const total_balance_days = balances.reduce(
    (sum, b) => sum + b.balance_days,
    0,
  );
  const has_any_balance = total_balance_days > 0;

  return (
    <>
      <GlassCard className="rounded-2xl p-5 lg:col-span-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
          <h2 className="text-[14.5px] font-bold">
            {t('employees.leave_balances.title')}
          </h2>
          {payout.post_payout !== undefined && has_any_balance && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => set_payout_open(true)}
              data-testid="employee-profile-leave-payout"
            >
              {t('employees.leave_balances.payout_button')}
            </Button>
          )}
        </div>

        {is_loading && (
          <>
            <span className="sr-only">
              {t('employees.leave_balances.loading')}
            </span>
            <div className="nv-skel" style={{ height: 48 }} />
          </>
        )}

        {state.status === 'entitlement_required' && (
          <EmptyState
            title={t('employees.leave_balances.entitlement_required_title')}
            description={t(
              'employees.leave_balances.entitlement_required_description',
            )}
          />
        )}

        {state.status === 'error' && (
          <EmptyState
            title={t('employees.leave_balances.error_title')}
            description={t('employees.leave_balances.error_description')}
            action={
              <Button variant="secondary" size="sm" onClick={retry}>
                {t('employees.leave_balances.retry')}
              </Button>
            }
          />
        )}

        {state.status === 'ready' && (
          <table
            className="nv-table"
            data-testid="employee-profile-leave-balances"
          >
            <thead>
              <tr>
                <th scope="col">{t('employees.leave_balances.column_type')}</th>
                <th scope="col" className="text-end">
                  {t('employees.leave_balances.column_days')}
                </th>
                {can_write && (
                  <th scope="col">
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {balances.map((row) => (
                <tr key={row.leave_type}>
                  <td>
                    {t(BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY[row.leave_type])}
                  </td>
                  <td className="text-end num">
                    {new Intl.NumberFormat(locale, {
                      minimumFractionDigits: row.balance_days % 1 === 0 ? 0 : 2,
                      maximumFractionDigits: 2,
                    }).format(row.balance_days)}
                  </td>
                  {can_write && (
                    <td>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => set_adjust_leave_type(row.leave_type)}
                        data-testid={`employee-profile-leave-adjust-${row.leave_type}`}
                      >
                        {t('employees.leave_balances.adjust_button')}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassCard>

      {payout.post_payout !== undefined && payout.fetch_estimate !== null && (
        <LeavePayoutDrawer
          open={payout_open}
          onOpenChange={set_payout_open}
          employee_id={employee_id}
          employee_name={employee_name}
          currency={currency}
          fetch_estimate={payout.fetch_estimate}
          post_payout={payout.post_payout}
          onSuccess={() => {
            set_payout_open(false);
            retry();
          }}
        />
      )}

      {can_write && readyEntity !== null && (
        <LeaveAdjustDrawer
          target={
            adjust_leave_type !== null
              ? buildOpeningBalanceTarget({
                  employee_id,
                  entity_id: readyEntity.entity_id,
                  leave_type: adjust_leave_type,
                  employee_name,
                  leave_type_label: t(
                    BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY[adjust_leave_type],
                  ),
                })
              : null
          }
          onOpenChange={(open) => {
            if (!open) set_adjust_leave_type(null);
          }}
          adjust={async (input): Promise<LeaveAdjustResult> => {
            try {
              const client = getTrpcClient();
              await client.payroll.adjustLeaveBalance.mutate({
                firm_id: readyEntity.firm_id,
                entity_id: readyEntity.entity_id,
                ...input,
              });
              retry();
              return { outcome: 'success' };
            } catch (err) {
              console.error('[wagepro-web] leave: adjust balance failed:', err);
              return { outcome: 'failure' };
            }
          }}
        />
      )}
    </>
  );
}
